import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "All Questions (10)" },
  { id: "general", label: "General & Pets" },
  { id: "services", label: "Services & Care" },
  { id: "booking", label: "Pricing & Booking" }
];

const FAQS = [
  {
    id: "faq-1",
    category: "general",
    q: "What is Woofsy?",
    a: "Woofsy is a doorstep pet grooming service that brings professional grooming right to your home, making the experience convenient, safe, and stress-free for both pets and pet parents."
  },
  {
    id: "faq-2",
    category: "general",
    q: "Which pets do you groom?",
    a: "We specialize exclusively in professional doorstep grooming & spa services for dogs of all breeds and ages in Pune."
  },
  {
    id: "faq-3",
    category: "services",
    q: "What services do you offer?",
    a: "Our services include bath & blow dry, spa treatments, full grooming, haircuts, nail trimming, ear cleaning, teeth cleaning, de-shedding, flea & tick treatments, and other grooming essentials."
  },
  {
    id: "faq-4",
    category: "booking",
    q: "How do I book an appointment?",
    a: "Booking is simple. You can schedule your pet's grooming appointment through our website or contact us via WhatsApp or phone."
  },
  {
    id: "faq-5",
    category: "booking",
    q: "How much does grooming cost?",
    a: "Our grooming services start from ₹699. Final pricing depends on your pet's breed, coat condition, grooming requirements, and the services selected."
  },
  {
    id: "faq-6",
    category: "services",
    q: "How long does a grooming session take?",
    a: "Most grooming appointments take between 60 and 120 minutes, depending on the service and your pet's grooming needs."
  },
  {
    id: "faq-7",
    category: "services",
    q: "What products do you use?",
    a: "We use premium, pet-safe grooming products that are suitable for different coat and skin types. If your pet has any allergies or special requirements, please let us know before the appointment."
  },
  {
    id: "faq-8",
    category: "general",
    q: "Do I need to be present during the grooming session?",
    a: "Yes. We recommend that a pet parent or a responsible adult is present throughout the grooming session for your pet's comfort and safety."
  },
  {
    id: "faq-9",
    category: "booking",
    q: "Can I reschedule or cancel my appointment?",
    a: "Yes. You can reschedule or cancel your appointment up to 24 hours before the scheduled time without any cancellation charges."
  },
  {
    id: "faq-10",
    category: "booking",
    q: "What if I'm not satisfied with the service?",
    a: "Your satisfaction is our priority. If you have any concerns, please contact us within 24 hours of your appointment, and our team will work with you to resolve the issue as quickly as possible."
  }
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredFaqs =
    activeCategory === "all"
      ? FAQS
      : FAQS.filter((f) => f.category === activeCategory);

  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 sm:py-32 px-5 sm:px-8 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10"
        >
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-woofsy-primary mb-3">
            Frequently Asked Questions
          </p>
          <h2 className="font-heading font-extrabold text-3xl sm:text-5xl text-woofsy-dark">
            Questions? We've got answers.
          </h2>
        </motion.div>

        {/* Category Pills Filter */}
        <div className="flex flex-wrap items-center justify-center gap-2.5 mb-12">
          {CATEGORIES.map((c) => {
            const isActive = activeCategory === c.id;
            return (
              <button
                key={c.id}
                onClick={() => {
                  setActiveCategory(c.id);
                  setOpen(0);
                }}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all shadow-sm ${
                  isActive
                    ? "bg-woofsy-primary text-white shadow-md shadow-orange-500/20"
                    : "glass text-woofsy-dark/70 hover:text-woofsy-primary border border-white/80"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* 2-Column Responsive Grid Layout (5 items on Left, 5 items on Right) */}
        <div className="grid md:grid-cols-2 gap-4.5 sm:gap-5 items-start">
          {filteredFaqs.map((f, i) => {
            const isOpen = open === i;

            return (
              <motion.div
                key={f.id}
                data-testid={`faq-item-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 4) * 0.05, duration: 0.35 }}
                whileHover={{ y: -2 }}
                className={`glass rounded-2xl overflow-hidden transition-all border ${
                  isOpen ? "border-woofsy-primary/40 shadow-lg bg-white/95" : "border-white/80 shadow-sm"
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left focus:outline-none"
                >
                  <span className="font-heading font-semibold text-sm sm:text-base text-woofsy-dark flex items-center gap-2.5">
                    <span className="text-woofsy-primary font-extrabold text-xs sm:text-sm shrink-0">
                      #{i + 1}
                    </span>
                    <span className="leading-snug">{f.q}</span>
                  </span>

                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      isOpen ? "bg-woofsy-primary text-white shadow-md" : "bg-woofsy-primary/15 text-woofsy-primary"
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <p className="px-4 sm:px-5 pb-5 pt-1 text-woofsy-dark/75 text-xs sm:text-sm leading-relaxed border-t border-slate-100/80">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
